import { useRTC } from "@/context/RTCContext";
import { Phone, PhoneX } from "@phosphor-icons/react";

/**
 * Floating incoming-call popup — HelloByFlow design tokens, no dark theme.
 * Shows caller avatar, name, department/title, call type, Accept / Decline.
 * Automatically disappears when caller cancels (RTCContext clears
 * incomingCall on `ring-stopped`).
 */
export default function IncomingMeetingCallToast() {
  const { incomingCall, acceptIncoming, declineIncoming } = useRTC();
  if (!incomingCall) return null;

  const from = incomingCall.from_user || {};
  const initials = (from.name || "?")
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div
      className="fixed bottom-6 right-6 z-[65] bg-white border border-[var(--hx-line)] rounded-2xl shadow-[0_25px_60px_-15px_rgba(11,21,38,0.35)] w-[360px] overflow-hidden animate-in slide-in-from-right"
      data-testid="incoming-meeting-toast"
    >
      <div className="px-4 pt-4 pb-3 flex items-center gap-3">
        {from.avatar_url ? (
          <img
            src={from.avatar_url}
            alt=""
            className="w-14 h-14 rounded-full object-cover ring-4 ring-[var(--hx-blue-soft)]"
          />
        ) : (
          <div className="w-14 h-14 rounded-full ring-4 ring-[var(--hx-blue-soft)] bg-[var(--hx-blue)] flex items-center justify-center text-white text-[16px] font-extrabold">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold tracking-[.16em] uppercase text-[var(--hx-blue)] mb-0.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--hx-blue)] animate-pulse" /> Incoming call
          </div>
          <div className="text-[15px] font-extrabold text-[var(--hx-ink)] truncate" data-testid="incoming-from-name">
            {from.name || "Unknown"}
          </div>
          {(from.department || from.title) && (
            <div className="text-[12px] text-[var(--hx-ink-3)] truncate" data-testid="incoming-from-dept">
              {from.title || from.department}
            </div>
          )}
          <div className="text-[11.5px] text-[var(--hx-ink-2)] truncate mt-0.5" data-testid="incoming-title">
            {incomingCall.title || "Internal call"}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 px-4 pb-4">
        <button
          onClick={declineIncoming}
          className="h-11 rounded-xl bg-white border border-[var(--hx-line)] text-[#B91C1C] hover:bg-[#FEF2F2] flex items-center justify-center gap-2 text-[13px] font-bold transition-colors"
          data-testid="incoming-decline-btn"
        >
          <PhoneX size={16} weight="fill" /> Decline
        </button>
        <button
          onClick={acceptIncoming}
          className="h-11 rounded-xl text-white flex items-center justify-center gap-2 text-[13px] font-bold transition-colors bg-[var(--hx-blue)] hover:bg-[var(--hx-blue-deep)] shadow-[0_8px_20px_-8px_rgba(37,99,235,0.5)]"
          data-testid="incoming-accept-btn"
        >
          <Phone size={16} weight="fill" /> Accept
        </button>
      </div>
    </div>
  );
}
